import { TaskComplianceIcon } from '@/Components/Common/Icons';
import useTaskCompliance from '@/Components/Hooks/useTaskCompliance';
import { translator } from '@vantiva-sa/homesight_humaui/Store/configStore';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useDashboardModal } from '../../DashboardModal';
import ComplianceCalendar from './ComplianceCalendar';
import ComplianceStats from './ComplianceStats';
import './TaskCompliance.less';
import TaskComplianceTable from './TaskComplianceTable';
import { useRegisterReportSection } from '../../reportUtils';

const currentSiteIconZip = (state) => state.data.siteIconFiles;
const currentSiteIconManifest = (state) => state.data.siteIconManifest;

const TaskComplianceSection = ({ useruuid, userTimezone }) => {
  const trans = useSelector(translator);
  const { openDashboardModal, closeDashboardModal } = useDashboardModal();
  const siteIconZip = useSelector(currentSiteIconZip);
  const siteIconManifest = useSelector(currentSiteIconManifest);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const tz = userTimezone || moment.tz.guess();
  const createdUrlsRef = useRef([]);

  useEffect(() => {
    let isCancelled = false;

    createdUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    createdUrlsRef.current = [];

    async function buildCategoryOptions() {
      if (!siteIconZip || !siteIconManifest || typeof siteIconZip.file !== 'function') {
        return;
      }
      const options = [];
      for (const mainCat of Object.keys(siteIconManifest)) {
        const subcats = siteIconManifest[mainCat];
        for (const subcat of Object.keys(subcats)) {
          const fileName = subcats[subcat];
          const file = siteIconZip.file(fileName);
          if (!file) continue;
          try {
            const blob = await file.async('blob');
            if (isCancelled) return;
            const blobToUse =
              blob.type && blob.type !== '' ? blob : new Blob([blob], { type: 'image/svg+xml' });
            const url = URL.createObjectURL(blobToUse);
            createdUrlsRef.current.push(url);
            options.push({ value: subcat, url });
          } catch (error) {
            console.error(`Error loading ${fileName}`, error);
          }
        }
      }
      if (!isCancelled) setCategoryOptions(options);
    }
    buildCategoryOptions();

    return () => {
      isCancelled = true;
      createdUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
      createdUrlsRef.current = [];
    };
  }, [siteIconZip, siteIconManifest]);

  const todayMonthKey = useMemo(() => moment().tz(tz).format('YYYY-MM'), [tz]);
  const [calendarMonth, setCalendarMonth] = useState(moment().tz(tz));
  const isCurrentMonth = calendarMonth.format('YYYY-MM') === todayMonthKey;

  // Single fetch: when on current month, only extend before month start by as many
  // days as needed to reach today-14 — avoids over-fetching when today is mid-month.
  // e.g. May 2  → extraPriorDays=13 (crosses into April)
  //      May 15 → extraPriorDays=0  (today-14=May 1, month start is enough)
  const extraPriorDays = useMemo(() => {
    if (!isCurrentMonth) return 0;
    return Math.max(0, 14 - (moment().tz(tz).date() - 1));
  }, [isCurrentMonth, tz]);

  const {
    complianceData: calendarData,
    isLoading: calendarLoading,
    error: calendarError,
    refetch: calendarRefetch,
  } = useTaskCompliance(useruuid, tz, calendarMonth, extraPriorDays);

  // Cache current-month data so stats remain stable while the calendar navigates.
  const statsDataRef = useRef(null);
  if (isCurrentMonth && calendarData) {
    statsDataRef.current = calendarData;
  }
  const statsData = statsDataRef.current;

  const handleDayClick = (date, tasks) => {
    setSelectedDate({ date, tasks });
  };

  const handleCloseModal = () => {
    setSelectedDate(null);
    closeDashboardModal();
  };

  const modalConfig = {
    title: (
      <div className="task-compliance-modal-title">
        <TaskComplianceIcon className="task-compliance-modal-title-icon" />
        <span>{trans('TASKS')}</span>
      </div>
    ),
    content: (
      <TaskComplianceTable
        tasks={selectedDate?.tasks || []}
        dateStr={selectedDate?.date || ''}
        timezone={tz}
        categoryOptions={categoryOptions}
      />
    ),
    onCancel: handleCloseModal,
  };

  useEffect(() => {
    if (selectedDate) {
      openDashboardModal(modalConfig);
    }
  }, [selectedDate]);

  // ── Last 7 days stats from current-month data (stable) ───────────────────
  const last7DaysStats = useMemo(() => {
    const dailyTasks = statsData?.dailyTasks;
    if (!Array.isArray(dailyTasks)) return { completed: 0, missed: 0, total: 0, rate: 0 };

    const today = moment().tz(tz);
    const yesterday = today.clone().subtract(1, 'days').format('YYYY-MM-DD');
    const sevenDaysAgo = today.clone().subtract(7, 'days').format('YYYY-MM-DD');

    let completed = 0;
    let missed = 0;
    let total = 0;

    dailyTasks.forEach(({ date, tasks }) => {
      if (date >= sevenDaysAgo && date <= yesterday) {
        tasks.forEach((t) => {
          if (t.status === 'completed') completed++;
          else if (t.status === 'expired') missed++;
          else return; // ongoing/scheduled are not yet finalized — exclude from denominator
          total++;
        });
      }
    });

    const rate = total > 0 ? (completed / total) * 100 : 0;
    return { completed, missed, total, rate };
  }, [statsData, tz]);

  // ── Previous 7 days stats (the week before last 7 days) ──────────────────
  const prev7DaysStats = useMemo(() => {
    const dailyTasks = statsData?.dailyTasks;
    if (!Array.isArray(dailyTasks)) return { completed: 0 };

    const today = moment().tz(tz);
    const fourteenDaysAgo = today.clone().subtract(14, 'days').format('YYYY-MM-DD');
    const eightDaysAgo = today.clone().subtract(8, 'days').format('YYYY-MM-DD');

    let completed = 0;

    dailyTasks.forEach(({ date, tasks }) => {
      if (date >= fourteenDaysAgo && date <= eightDaysAgo) {
        tasks.forEach((t) => {
          if (t.status === 'completed') completed++;
        });
      }
    });

    return { completed };
  }, [statsData, tz]);

  // ── Trend: % change in completed task count between the two 7-day windows.
  // e.g. prev=3 completed, last=6 completed → +100%
  //      prev=3 completed, last=0 completed → -100%
  // Returns null (sentinel) when prev window has 0 completed but this week has some.
  const complianceRate = useMemo(() => {
    const prevCompleted = prev7DaysStats.completed;
    const lastCompleted = last7DaysStats.completed;
    if (prevCompleted === 0 && lastCompleted === 0) return 0;
    if (prevCompleted === 0) return null;
    return ((lastCompleted - prevCompleted) / prevCompleted) * 100;
  }, [last7DaysStats, prev7DaysStats]);

  const complianceTrend = useMemo(() => {
    if (complianceRate === null) return 'up';
    if (complianceRate > 0) return 'up';
    if (complianceRate < 0) return 'down';
    return 'neutral';
  }, [complianceRate]);

  // Calendar-specific derived data
  const dailyTasks = calendarData?.dailyTasks || [];

  // Register report-ready compliance data: last-7-day stats (for the pie chart)
  // plus the month's daily tasks (for the calendar view).
  const periodLabel = trans('LAST_N_DAYS').replace('{{days}}', 7);
  useRegisterReportSection(
    'taskCompliance',
    {
      stats: {
        completed: last7DaysStats.completed,
        missed: last7DaysStats.missed,
        total: last7DaysStats.total,
        rate: last7DaysStats.rate,
      },
      periodLabel,
      dailyTasks,
      monthISO: calendarMonth.format('YYYY-MM'),
      timezone: tz,
    },
    [last7DaysStats, dailyTasks, calendarMonth, tz]
  );

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="compliance-section">
      {/* Title row – no card background, just heading */}
      <div className="compliance-title-row">
        <TaskComplianceIcon className="compliance-title-icon" />
        <h3 className="section-title">{trans('TASK_COMPLIANCE')}</h3>
      </div>

      {/* Main content: calendar card + stats row */}
      <div className="compliance-layout">
        {/* Calendar Card — always mounted, overlay spinner on refetch */}
        <div className="compliance-calendar-card">
          {calendarLoading && (
            <div className="compliance-calendar-overlay">
              <div className="spinner"></div>
            </div>
          )}
          {calendarError && !calendarData && (
            <div className="compliance-calendar-error">
              <p>{trans('ERROR_LOADING_DATA')}</p>
              <button onClick={calendarRefetch} className="retry-button">
                {trans('RETRY')}
              </button>
            </div>
          )}
          <ComplianceCalendar
            dailyTasks={dailyTasks}
            userTimezone={tz}
            onDayClick={handleDayClick}
            currentMonth={calendarMonth}
            onMonthChange={setCalendarMonth}
          />
        </div>

        {/* Stats Summary Card — always shows current month stats */}
        <ComplianceStats
          completedTasks={last7DaysStats.completed}
          missedTasks={last7DaysStats.missed}
          totalTasks={last7DaysStats.total}
          complianceRate={complianceRate}
          periodLabel={trans('LAST_N_DAYS').replace('{{days}}', 7)}
          trend={complianceTrend}
        />
      </div>

      {/* Day Detail Modal */}
      {/* <InfoModal
        open={!!selectedDate}
        onCancel={handleCloseModal}
        footer={null}
        width={680}
        destroyOnClose
        className="task-compliance-modal"
        rootClassName="task-compliance-modal-root"
        title={
          <div className="task-compliance-modal-title">
            <TaskComplianceIcon className="task-compliance-modal-title-icon" />
            <span>{trans('TASKS')}</span>
          </div>
        }
      >
        {selectedDate && (
          <TaskComplianceTable
            tasks={selectedDate.tasks || []}
            dateStr={selectedDate.date}
            timezone={tz}
            categoryOptions={categoryOptions}
          />
        )}
      </InfoModal> */}
    </div>
  );
};

TaskComplianceSection.propTypes = {
  useruuid: PropTypes.string.isRequired,
  userTimezone: PropTypes.string,
};

export default TaskComplianceSection;
